import React from 'react'

// Components
import {
  Box,
  Paper,
  CardContent,
  Typography,
  Chip,
  useMediaQuery,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloseIcon from '@mui/icons-material/Close'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'

// Logic
import useFiltersLogic, { dateRanges } from './Filters.logic'

// Components
import DateInput from '../../DateInput/DateInput'
import StyledButton from '../../StyledButton/StyledButton'
import MultiSelectFilter from '../MultiSelectFilter/MultiSelectFilter'

// Styles
import styles from './Filters.module.scss'

// Types
import { FiltersProps, FilterChipProps } from './Filters.types'

const Filters: React.FC<FiltersProps> = (props) => {
  const { filters, goBack, goForward, canGoBack, canGoForward, onReset } = props

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  const {
    expanded,
    selectedDateRange,
    activeFiltersCount,
    hasActiveFilters,
    surgeonNames,
    manufacturerNames,
    procedureNames,
    locationNames,
    assetTypeNames,
    dispositionNames,
    toggleExpanded,
    clearFilter,
    handleDateChange,
    handleDateRangeChange,
    handleFilterChange,
  } = useFiltersLogic(props)

  const FilterChip: React.FC<FilterChipProps> = ({ label, onDelete }) => {
    const isDateChip = label.startsWith('Start:') || label.startsWith('End:')

    return (
      <Chip
        label={label}
        onDelete={isDateChip ? undefined : onDelete}
        deleteIcon={isDateChip ? undefined : <CloseIcon fontSize="small" />}
        size="small"
        className={`${styles.filterChip} ${isMobile ? styles.mobile : ''}`}
      />
    )
  }

  const renderFilterChips = () => {
    const chips: JSX.Element[] = []

    // Add date range chip if a preset is selected
    if (filters.dateRange && filters.dateRange !== 'custom') {
      const selectedRangeLabel = dateRanges.find(
        (range) => range.value === filters.dateRange
      )?.label
      if (selectedRangeLabel) {
        chips.push(
          <FilterChip
            key="dateRange"
            label={`Date Range: ${selectedRangeLabel}`}
            onDelete={() => props.onFiltersChange({ dateRange: 'custom' })}
          />
        )
      }
    }

    // Add status chip if not default
    if (filters.status !== 'Submitted') {
      chips.push(
        <FilterChip
          key="status"
          label={`Status: ${filters.status}`}
          onDelete={() => props.onFiltersChange({ status: 'Submitted' })}
        />
      )
    }

    if (filters.startDate)
      chips.push(
        <FilterChip
          key="startDate"
          label={`Start: ${filters?.startDate?.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          })}`}
          onDelete={() => clearFilter('startDate')}
        />
      )
    if (filters.endDate)
      chips.push(
        <FilterChip
          key="endDate"
          label={`End: ${filters?.endDate?.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          })}`}
          onDelete={() => clearFilter('endDate')}
        />
      )

    if (filters.surgeonName?.length) {
      filters.surgeonName.forEach((name) => {
        chips.push(
          <FilterChip
            key={`surgeon-${name}`}
            label={`Surgeon: ${name}`}
            onDelete={() =>
              handleFilterChange(
                'surgeonName',
                filters.surgeonName?.filter((n) => n !== name) || null
              )
            }
          />
        )
      })
    }

    if (filters.procedure?.length) {
      filters.procedure.forEach((proc) => {
        chips.push(
          <FilterChip
            key={`procedure-${proc}`}
            label={`Procedure: ${proc}`}
            onDelete={() =>
              handleFilterChange(
                'procedure',
                filters.procedure?.filter((p) => p !== proc) || null
              )
            }
          />
        )
      })
    }

    if (filters.location?.length) {
      filters.location.forEach((loc) => {
        chips.push(
          <FilterChip
            key={`location-${loc}`}
            label={`Location: ${loc}`}
            onDelete={() =>
              handleFilterChange(
                'location',
                filters.location?.filter((l) => l !== loc) || null
              )
            }
          />
        )
      })
    }

    if (filters.companyName?.length) {
      filters.companyName.forEach((spec) => {
        chips.push(
          <FilterChip
            key={`companyName-${spec}`}
            label={`Manufacturer: ${spec}`}
            onDelete={() =>
              handleFilterChange(
                'companyName',
                filters.companyName?.filter((s) => s !== spec) || null
              )
            }
          />
        )
      })
    }

    if (filters.assetType?.length) {
      filters.assetType.forEach((type) => {
        chips.push(
          <FilterChip
            key={`assetType-${type}`}
            label={`Product Type: ${type}`}
            onDelete={() =>
              handleFilterChange(
                'assetType',
                filters.assetType?.filter((t) => t !== type) || null
              )
            }
          />
        )
      })
    }

    if (filters.disposition?.length) {
      filters.disposition.forEach((dis) => {
        chips.push(
          <FilterChip
            key={`disposition-${dis}`}
            label={`Disposition: ${dis}`}
            onDelete={() =>
              handleFilterChange(
                'disposition',
                filters.disposition?.filter((d) => d !== dis) || null
              )
            }
          />
        )
      })
    }

    return chips
  }

  const chips = renderFilterChips()

  return (
    <Paper elevation={2} className={styles.filtersContainer}>
      <CardContent
        className={`${styles.cardContent} ${isMobile ? styles.mobile : ''} ${
          expanded ? styles.expanded : ''
        }`}
      >
        <Box
          className={`${styles.headerContainer} ${
            isMobile ? styles.mobile : ''
          }`}
        >
          {/* Header with title and expand button */}
          <Box
            className={`${styles.titleContainer} ${
              isMobile ? styles.mobile : ''
            }`}
          >
            <Typography variant="subtitle1" className={styles.title}>
              Filters
              {activeFiltersCount > 0 && (
                <Box component="span" className={styles.activeFiltersBadge}>
                  {activeFiltersCount}
                </Box>
              )}
            </Typography>
            <StyledButton
              label={expanded ? 'Hide' : 'Show'}
              onClick={toggleExpanded}
              size="small"
              variant="outlined"
              color="primary"
              className={styles.expandButton}
              startIcon={
                expanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )
              }
            />
          </Box>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <Box
              className={`${styles.chipsContainer} ${
                isMobile ? styles.mobile : styles.desktop
              }`}
            >
              {chips}
            </Box>
          )}

          {/* Buttons Area */}
          <Box
            className={`${styles.buttonsArea} ${isMobile ? styles.mobile : ''}`}
          >
            {/* Back/Forward Buttons */}
            <IconButton
              aria-label="Undo filter change"
              onClick={goBack}
              disabled={!canGoBack}
              size="small"
              color="primary"
            >
              <UndoIcon />
            </IconButton>
            <IconButton
              aria-label="Redo filter change"
              onClick={goForward}
              disabled={!canGoForward}
              size="small"
              color="primary"
            >
              <RedoIcon />
            </IconButton>

            {/* Reset Button */}
            <StyledButton
              label="Reset"
              onClick={onReset}
              size="small"
              variant="outlined"
              disabled={!hasActiveFilters}
              fullWidth={isMobile}
            />
          </Box>
        </Box>
      </CardContent>
      <Collapse in={expanded} timeout={300}>
        <CardContent
          className={`${styles.expandedContent} ${
            isMobile ? styles.mobile : ''
          }`}
        >
          <Box
            className={`${styles.filtersGrid} ${isMobile ? styles.mobile : ''}`}
          >
            <Box
              className={`${styles.dateSection} ${
                isMobile ? styles.mobile : ''
              }`}
            >
              <Box
                className={`${styles.dateRow} ${isMobile ? styles.mobile : ''}`}
              >
                <Box className={styles.dateRangeSelect}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="date-range-select-label">
                      Date Range
                    </InputLabel>
                    <Select
                      labelId="date-range-select-label"
                      id="date-range-select"
                      value={selectedDateRange}
                      label="Date Range"
                      onChange={handleDateRangeChange}
                    >
                      {dateRanges.map((range) => (
                        <MenuItem key={range.value} value={range.value}>
                          {range.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box className={styles.dateInput}>
                  <DateInput
                    label="Start Date"
                    date={filters.startDate}
                    handleDateChange={(date) =>
                      handleDateChange('startDate', date)
                    }
                  />
                </Box>
                <Box className={styles.dateInput}>
                  <DateInput
                    label="End Date"
                    date={filters.endDate}
                    handleDateChange={(date) =>
                      handleDateChange('endDate', date)
                    }
                  />
                </Box>
              </Box>
            </Box>

            <MultiSelectFilter
              label="Surgeon Name"
              options={surgeonNames}
              selectedValues={filters.surgeonName}
              onChange={(values) => handleFilterChange('surgeonName', values)}
              compact={isMobile}
              disabled={surgeonNames.length === 0}
            />
            <MultiSelectFilter
              compact={isMobile}
              label="Manufacturer"
              options={manufacturerNames}
              selectedValues={filters.companyName}
              onChange={(values) => handleFilterChange('companyName', values)}
              disabled={manufacturerNames.length === 0}
            />
            <MultiSelectFilter
              compact={isMobile}
              label="Procedure"
              options={procedureNames}
              selectedValues={filters.procedure}
              onChange={(values) => handleFilterChange('procedure', values)}
              disabled={procedureNames.length === 0}
            />
            <MultiSelectFilter
              compact={isMobile}
              label="Location"
              options={locationNames}
              selectedValues={filters.location}
              onChange={(values) => handleFilterChange('location', values)}
              disabled={locationNames.length === 0}
            />
            <MultiSelectFilter
              compact={isMobile}
              label="Product Type"
              options={assetTypeNames}
              selectedValues={filters.assetType}
              onChange={(values) => handleFilterChange('assetType', values)}
              disabled={assetTypeNames.length === 0}
            />
            <MultiSelectFilter
              compact={isMobile}
              label="Disposition"
              options={dispositionNames}
              selectedValues={filters.disposition}
              onChange={(values) => handleFilterChange('disposition', values)}
              disabled={dispositionNames.length === 0}
            />
          </Box>
        </CardContent>
      </Collapse>
    </Paper>
  )
}

export default Filters
